/* eslint react/prop-types: 0 */

import React from 'react';

import { transactionsSelectors } from 'app-components/flux/transactions';
import { preProcess } from 'app-components/flux/transactions-processing';
import { getLogger, tracker } from 'app-components/utils/core';

import MatchIcon from '@mui/icons-material/CompareArrows';
import Typography from '@mui/material/Typography';

import trackEventTTI from 'utils/trackEventTTI';

import MiniTxnCard from './MiniTxnCard';
import { processGuides } from './utils';

const log = getLogger('data/useTxnActions');

const { addMatches, preProcessDelete, preProcessUpdate } = preProcess;

const locationsMap = new Map([
  ['registerWrapper_TxPage', 'transactions'],
  ['Reg_Budgets', 'budgets'],
  ['Reg_SpendingGraph', 'spending'],
]);

export const performTransactionAction = (action, props) => {
  log.log('Performing Transaction Action ', action);
  props.performTransactionAction(action, { context: props.wrapperId });
};

export const unMatchTransaction = (txn, props) => {
  log.log('UNMATCH TRANSACTION', txn);

  props.performTransactionAction({ action: 'unmatch', id: txn.id }, { context: props.wrapperId });
};

export const matchTransactions = (txns, props) => {
  // eslint-disable-line
  log.log('MATCH TRANSACTIONS', txns);
  if (txns.length !== 2) {
    return false;
  }

  props.performTransactionAction(
    { action: 'match', id: txns[0].id, otherId: txns[1].id },
    { context: props.wrapperId },
  );

  tracker.track(tracker.events.txnManualMatch, {
    state: 'complete',
  });
};

export const getMatch = (candidates, txn) => candidates.find((x) => x.guid === txn.guid);

const deniedMatches = [];

// Send info to MixPanel for this action
export const sendTrackingInfo = (txnPack, props) => {
  const { allTxnsToDelete, allTxnsToUpdate, allTxnsToCreate } = txnPack;
  const location = locationsMap.get(props.wrapperId);

  // Send tracking data for deleted transactions
  allTxnsToDelete?.forEach((x) => {
    const trackingProps = transactionsSelectors.getTrackingProps(undefined, x);
    trackEventTTI(tracker.events.txnDelete, { ...trackingProps, location });
  });
  // Send tracking data for updated transactions
  allTxnsToUpdate?.forEach((x) => {
    const trackingProps = transactionsSelectors.getTrackingProps(undefined, x);
    if (x.isDeleted) {
      trackEventTTI(tracker.events.txnDelete, { ...trackingProps, location });
    } else {
      trackEventTTI(tracker.events.txnSave, { ...trackingProps, location });
    }
  });
  allTxnsToCreate?.forEach((x) => {
    const trackingProps = transactionsSelectors.getTrackingProps(undefined, x);
    trackEventTTI(tracker.events.txnSave, { ...trackingProps, location });
  });
};

export const sendTransactionPack = async (txnPack, props) => {
  // send mixPanel events
  sendTrackingInfo(txnPack, props);

  log.log('TRANSACTION PACK TO SEND TO ACTIONS/SAGAS ', txnPack);
  const { allTxnsToDelete, allTxnsToUpdate, allTxnsToCreate, extraActions } = txnPack;
  const result = [];
  try {
    if (allTxnsToDelete?.length === 1) {
      // better error handling for non-batch request
      result.push(
        await props.deleteTransaction(allTxnsToDelete[0], {
          context: props.wrapperId,
          undo: !props.robotMode && {
            userMessage: props.messages?.transactionDeletedMessage ?? 'Transaction deleted.',
          },
        }),
      );
    } else if (allTxnsToDelete?.length > 1) {
      result.push(
        await props.batchTransactions(allTxnsToDelete, {
          context: props.wrapperId,
          undo: !props.robotMode && {
            userMessage: props.messages?.transactionsDeletedMessage ?? 'Transactions deleted.',
          },
        }),
      );
    }

    if (allTxnsToUpdate?.length === 1) {
      result.push(
        await props.updateTransaction(allTxnsToUpdate[0], {
          context: props.wrapperId,
          undo: !props.robotMode && {
            userMessage: props.messages?.transactionUpdatedMessage ?? 'Transaction updated.',
          },
        }),
      );
    } else if (allTxnsToUpdate?.length > 1) {
      result.push(
        await props.batchTransactions(allTxnsToUpdate, {
          context: props.wrapperId,
          undo: !props.robotMode && {
            userMessage: props.messages?.transactionsUpdatedMessage ?? 'Transactions updated.',
          },
        }),
      );
    }

    if (allTxnsToCreate?.length === 1) {
      result.push(
        await props.createTransaction(allTxnsToCreate[0], {
          context: props.wrapperId,
          undo: !props.robotMode && {
            userMessage: props.messages?.transactionCreatedMessage ?? 'Transaction created.',
          },
        }),
      );
    } else if (allTxnsToCreate?.length > 1) {
      result.push(
        await props.batchTransactions(allTxnsToCreate, {
          context: props.wrapperId,
          undo: !props.robotMode && {
            userMessage: props.messages?.transactionCreatedMessage ?? 'Transactions created.',
          },
        }),
      );
    }
    const resources = result
      .map((response) => {
        if (response?.resources) {
          return response.resources;
        }
        return [
          {
            id: response.data.id || response.resource.id,
            clientId: response.data.clientId || response.resource.clientId,
            resource: response.resource,
          },
        ];
      })
      ?.flat();
    props.callback?.(resources);
    props.resolve?.(resources);
  } catch (error) {
    log.error('Error sending transaction pack', error);
    props.callback?.(false);
    props.reject?.(error);
  }

  // extra actions are objects { action, delay }
  if (extraActions) {
    extraActions.forEach((item) => setTimeout(() => props.genericDispatch(item.action), item.delay));
  }
};

export const processTransactionPack = (txnPack, props) => {
  const { allMatches } = txnPack;

  // User already went through any prior warnings, but we check now to see if there are any matched txns
  // in 'allMatches' which we will ask the user if they want to use them instead of the transaction in
  // create package (use matched instead of new transfer txn)
  //
  // This is an all or nothing (all matches accepted, or none)
  // TODO create a match dialog that lets you select each one, but do this when we have some sort of
  // TODO autosave that lets you create multiple matching transfers at once
  //
  if (allMatches && allMatches.length > 0) {
    const matchesToKeep = [];
    const message = [];
    let forceWarn = false;
    allMatches.forEach((matchObject) => {
      if (
        deniedMatches.find(
          (matchPair) =>
            matchPair &&
            matchPair.includes &&
            (matchPair.includes(matchObject.baseTxn?.id) || matchPair.includes(matchObject.baseTxn?.clientId)) &&
            matchPair.includes(matchObject.possibleMatch?.id),
        )
      ) {
        // don't keep this match
        log.log('Ignoring already declined match');
        return;
      }

      forceWarn = forceWarn || matchObject.forceWarn; // if one has it, they all have it
      const msg1 = (
        <Typography style={{ marginBottom: 15, fontSize: '16px' }}>
          We found similar activity for the following. Do you want to connect these transactions?
        </Typography>
      );

      const msg2 = (
        <div style={{ display: 'flex', padding: '7px 0 7px 0' }}>
          <MiniTxnCard style={{ flex: 2, maxWidth: 250 }} txn={matchObject.possibleMatch} />
          <div style={{ flex: 0, minWidth: 25, width: 25, margin: 'auto', textAlign: 'center' }}>
            <MatchIcon />
          </div>
          <MiniTxnCard style={{ flex: 2, maxWidth: 250 }} txn={matchObject.baseTxn} />
        </div>
      );
      message.push(
        <div style={{ padding: '0px 7px 0px 7px' }} key={`matchDlg:${matchObject.possibleMatch.payee}`}>
          {' '}
          {msg1} {msg2}{' '}
        </div>,
      );
      matchesToKeep.push(matchObject);
    });

    if (matchesToKeep.length > 0) {
      txnPack.allMatches = matchesToKeep; // eslint-disable-line no-param-reassign

      // Only show dialogs when forced (preprocess logic determined we should)
      if (forceWarn) {
        props.dialogAlert(
          'Link transfers',
          message,
          (btnObj) => {
            if (btnObj.btnPressed === 'Link') {
              if (addMatches(txnPack)) {
                sendTransactionPack(txnPack, props);
              } else {
                props.dialogAlert(
                  'Error: Matching operation failed',
                  'You will need to create the match manually',
                  null,
                  ['Got It'],
                );
              }
            } else if (btnObj.btnPressed === "Don't Link") {
              sendTransactionPack(txnPack, props);
              allMatches.forEach((matchObject) => {
                deniedMatches.push([
                  matchObject.baseTxn?.id || matchObject.baseTxn?.clientId,
                  matchObject.possibleMatch?.id,
                ]);
              });
            }
          },
          ['Cancel', 'Link'],
          'none',
          false,
          false,
          "Don't Link",
        );
      } else {
        addMatches(txnPack);
        sendTransactionPack(txnPack, props);
      }
    } else {
      sendTransactionPack(txnPack, props);
    }
  } else {
    sendTransactionPack(txnPack, props);
  }
};

export const errorsAndWarningsConfirm = (warnings, errors, cb, props) => {
  // Show Errors
  if (errors && errors.length > 0) {
    const message = [<p key='titleerr+=+'> </p>];
    errors.forEach((x, index) => {
      const val = (
        <Typography variant='titleRegular' key={x}>
          {errors.length > 1 ? `(${index + 1}) ${x}` : x} <br />
        </Typography>
      );
      message.push(val);
    });

    props.dialogAlert(
      errors.length > 1 ? 'Some errors occurred' : 'An error occurred',
      message,
      () => {
        cb(false);
      },
      ['Got it'],
      'error',
    );
    return null;
  }

  // Show warnings
  /* eslint-disable react/no-array-index-key */
  if (warnings && warnings.length > 0) {
    const message = [];
    message.push(
      <Typography variant='titleRegular' key={`uniquekeygoeshere${warnings.length}`}>
        {' '}
        Before you continue... <br />{' '}
      </Typography>,
    );
    warnings.forEach((x, index) => {
      message.push(
        <React.Fragment key={`da_msg_p${x}${index}`}>
          {' '}
          <Typography> {` - ${x} `} </Typography>{' '}
        </React.Fragment>,
      );
    });
    props.dialogAlert(
      warnings.length > 1 ? 'Some warnings before you continue...' : 'A warning before you continue...',
      message,
      (btnObj) => {
        if (btnObj.btnPressed === 'Continue') {
          cb(true);
        }
        cb(false);
      },
      ['Cancel', 'Continue'],
      'warning',
    );
    return null;
  }

  return cb(true);
};

export const deleteTransactions = (txns, props) => {
  log.debug('QDATA Delete Transactions: ', txns);
  const { callback } = props;
  const { allTxnsToDelete, allTxnsToUpdate, warnings, errors } = preProcessDelete({
    txns,
    txnsByAccountId: props.txnsByAccountId,
    isUpdate: false,
    featureFlags: props.featureFlags,
    state: props.state,
  });
  log.debug(
    'QDATA',
    '\nWarnings: ',
    warnings,
    '\nErrors: ',
    errors,
    '\ntxnsToUpdate: ',
    allTxnsToUpdate,
    '\ntxnsToDelete',
    allTxnsToDelete,
  );

  errorsAndWarningsConfirm(
    warnings,
    errors,
    async (confirm) => {
      if (confirm) {
        // track deletes/updates
        sendTrackingInfo({ allTxnsToUpdate, allTxnsToDelete }, props);

        // fire off action to update the transactions that need to be updated
        //
        if (allTxnsToUpdate?.length === 1) {
          // better error handling for non-batch request
          await props.updateTransaction(allTxnsToUpdate[0], {
            context: props.wrapperId,
            undo: !props.robotMode && {
              userMessage: props.messages?.transactionUpdatedMessage ?? 'Transaction updated.',
            },
          });
        } else if (allTxnsToUpdate?.length > 1) {
          await props.batchTransactions(allTxnsToUpdate, {
            context: props.wrapperId,
            undo: !props.robotMode && {
              userMessage: props.messages?.transactionsUpdatedMessage ?? 'Transactions updated.',
            },
          });
        }

        if (allTxnsToDelete?.length === 1) {
          // better error handling for non-batch request
          await props.deleteTransaction(allTxnsToDelete[0], {
            context: props.wrapperId,
            undo: !props.robotMode && {
              userMessage: props.messages?.transactionDeletedMessage ?? 'Transaction deleted.',
            },
          });
        } else if (allTxnsToDelete?.length > 1) {
          await props.batchTransactions(allTxnsToDelete, {
            context: props.wrapperId,
            undo: !props.robotMode && {
              userMessage: props.messages?.transactionsDeletedMessage ?? 'Transactions deleted.',
            },
          });
        }
        callback?.();
        // TODO: Should this return anything?
        props.resolve?.();
      }
    },
    props,
  );
};

export const saveTransactions = (txns, props) => {
  log.debug('QDATA Save Transactions:', txns);
  const { warnings, errors, guides, ...txnPack } = preProcessUpdate({
    txns,
    txnsByAccountId: props.txnsByAccountId,
    featureFlags: props.featureFlags,
  });
  log.debug('QDATA', '\nWarnings: ', warnings, '\nErrors: ', errors, '\ntxnPack: ', txnPack);

  //
  // in some cases during a multi-transaction update, a transfer transaction might be getting updated
  // along with one of it's children.  This can result in a new child transaction being created, and the
  // previous transaction being deleted, but also the previous transaction may attempt to update itself
  // effectively undeleting itself.  We check for any updates conflicting with deletes, and we remove
  // the updates if there is a conflict (delete wins)
  //
  // const newTxnPack = removeConflicts(txnPack);
  const newTxnPack = txnPack;
  // robotMode used to hide the modals displaying errors / warnings
  if (props.robotMode) {
    // if errors present & callback, call callback w/ false & don't update
    if (errors && errors.length > 0) {
      // NOTE: This looks to be a bug that has been there since robotMode was introduced. The
      //       callback is called with false, but the transaction pack is still processed.
      //       If this gets fixed, make sure to correctly handle the props.reject if it is
      //       passed in.
      //
      props.callback?.(false);
    }
    // otherwise, update and call callback w/ true
    processTransactionPack(newTxnPack, props);
    processGuides(guides);

    return null;
  }
  // only call errorsAndWarnings if no robotMode
  errorsAndWarningsConfirm(
    warnings,
    errors,
    (confirm) => {
      if (confirm) {
        processTransactionPack(newTxnPack, props);
        processGuides(guides);
        return null;
      }
      return null;
    },
    props,
  );

  return null;
};
